import { Asset, FolderContent, VisualSearchMatch } from '@/api/generatedApi';
import { FolderBreadcrumbs } from '@/components/AssetsPage/FolderBreadcrumbs';
import { DataGridLazy } from '@/components/DataGrid/DataGridLazy';
import { SearchInput } from '@/components/Search/SearchInput';
import { useTextSearch } from '@/hooks/api/crossTenant/textsearch/useTextSearch';
import { useFetchFolderContents } from '@/hooks/api/folders/useFetchFolderContents';
import { useOptimisticFolderName } from '@/hooks/useOptimisticFolderName';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { DATA_GRID_LAZY_PAGE_SIZE } from '../DataGrid/definitions';
import { columns } from './AssetSelectionModal/columnDefinitions';

export const AssetSelectionModal = ({
  open,
  onClose,
  onAssetSelect,
  onFolderSelect,
  getRowClassName,
  title = 'Select Asset',
  description = 'Choose an asset to use.',
}: {
  open: boolean;
  onClose: () => void;
  onAssetSelect: (asset: Asset) => void;
  onFolderSelect?: (folder: { id: string; name: string }) => void;
  getRowClassName?: (parameters: { row: FolderContent }) => string;
  title?: string;
  description?: string;
}) => {
  const gridApi = useGridApiRef();
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>();
  const [folderHistory, setFolderHistory] = useState<Array<{ id: string; name: string }>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);

  const { fetchData, error: folderError } = useFetchFolderContents({ folderId: currentFolderId });
  const { setOptimisticFolder } = useOptimisticFolderName();

  const {
    data: searchData,
    error: searchError,
    isLoading: searchLoading,
  } = useTextSearch({
    searchQuery,
    page: 1,
    perPage: DATA_GRID_LAZY_PAGE_SIZE,
    enabled: isSearchMode && searchQuery.trim().length > 0,
  });

  const handleRowClick = (parameters: { row: FolderContent }) => {
    if (parameters.row.contentType === 'asset') {
      onAssetSelect(parameters.row as Asset);
      onClose();
    } else {
      const folder = parameters.row as { id: string; name: string; contentType: 'folder' };
      setOptimisticFolder(folder.id, folder.name);
      if (onFolderSelect) {
        onFolderSelect(folder);
      }
      setCurrentFolderId(folder.id);
      setFolderHistory(previous => [...previous, { id: folder.id, name: folder.name }]);
    }
  };

  const handleBreadcrumbNavigate = (folderId?: string, folderName?: string) => {
    setCurrentFolderId(folderId);
    if (folderId) {
      const index = folderHistory.findIndex(f => f.id === folderId);
      if (index !== -1) {
        setFolderHistory(folderHistory.slice(0, index + 1));
      } else if (folderName) {
        setFolderHistory([...folderHistory, { id: folderId, name: folderName }]);
      }
    } else {
      setFolderHistory([]);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      setIsSearchMode(true);
    } else {
      setIsSearchMode(false);
    }
  };

  const handleModalClose = () => {
    setCurrentFolderId(undefined);
    setFolderHistory([]);
    setSearchQuery('');
    setIsSearchMode(false);
    onClose();
  };

  const currentError = isSearchMode ? searchError : folderError;
  const currentLoading = isSearchMode ? searchLoading : false;

  const fetchCurrentData = async (parameters: { page: number; perPage: number }) => {
    if (isSearchMode) {
      if (searchData?.matches) {
        return {
          contents: searchData.matches.map((match: VisualSearchMatch) => ({
            ...match.asset,
            contentType: 'asset' as const,
          })),
          pageData: {
            total: searchData.pageData?.total || 0,
          },
        };
      }
      return { contents: [], pageData: { total: 0 } };
    } else {
      return fetchData(parameters);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth='md'
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            minHeight: '600px',
          },
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>{description}</Box>
        <Box sx={{ mb: 2 }}>
          <SearchInput initialValue={searchQuery} onSearch={handleSearch} placeholder='Search assets...' />
        </Box>
        {!isSearchMode && (
          <Box sx={{ mb: 1 }}>
            <FolderBreadcrumbs folderId={currentFolderId} showRootFolder onNavigate={handleBreadcrumbNavigate} />
          </Box>
        )}
        <Box sx={{ height: '400px', width: '100%' }}>
          <DataGridLazy<FolderContent, { contents: FolderContent[] }>
            apiRef={gridApi}
            columns={columns}
            fetchRows={fetchCurrentData}
            dataKey='contents'
            rowHeight={80}
            columnHeaderHeight={40}
            loading={currentLoading}
            localeText={{
              noRowsLabel: isSearchMode ? 'No search results found' : 'No contents available',
            }}
            error={currentError}
            density='compact'
            disableFilter={true}
            getRowClassName={getRowClassName}
            onRowClick={handleRowClick}
            sx={{
              '& .MuiDataGrid-cell': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'secondary.hover',
              },
              '& .MuiDataGrid-row.disabled-row': {
                opacity: 0.5,
                cursor: 'not-allowed',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              },
              '& .MuiDataGrid-row.disabled-row .MuiDataGrid-cell': {
                cursor: 'not-allowed',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleModalClose} color='primary'>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
